import { PropsWithChildren, useState } from 'react';
import PropTypes from 'prop-types';
import { post } from '../../core/client';
import { IGridPagedModel } from '../../models/shared/GridModels';
import Alert, { AlertColor } from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import { ClearButton, CustomButton, CustomIconButton, SearchButton } from '../../components/buttons';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Select from '../../components/inputs/select';
import Input from '../../components/inputs/text-field-input';
import Skeleton from '@mui/material/Skeleton';
import DataTable from '../../components/data-table';
import { CancelSharp } from '@mui/icons-material';
import { IsNullOrEmpty } from '../../core/utility';
import { CancelledAvQueryResult } from '../../models/cancel-purchcase';
import { DataTableCol } from '../../components/data-table/Models';

interface SearchModel extends IGridPagedModel {
    criteria: number;
    no: string;
    avNo: string;
}

interface AlertMessage {
    status: AlertColor;
    text: string;
}

const queryTypesData: any = [
    { Name: 'TC Kimlik No', Id: 1 },
    { Name: 'KoçAilem No', Id: 3 },
    { Name: 'Av No', Id: 4 },
];

const initialGrid = {
    totalCount: 0,
    items: [],
};

const initialState: SearchModel = {
    pageNumber: 0,
    pageSize: 5,
    order: true,
    orderBy: '',
    criteria: 1,
    no: '',
    avNo: ''
};

const Search = () => {

    // states    
    const [gridRequest, setGridRequest] = useState<SearchModel>(initialState);
    const [loading, setloading] = useState(false);
    const [errorText, setErrorText] = useState('');
    const [message, setMessage] = useState<AlertMessage | null>(null);
    const [grid, setGrid] = useState(initialGrid);
    const [dataSourceLoaded, setDataSourceLoaded] = useState(false);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [selectedTradeNo, setSelectedTradeNo] = useState<string>('');

    // methods
    const loadGridAvInfo = async (search: SearchModel) => {

        setErrorText('');
        setMessage(null);

        if (!validate(search)) return;

        setDataSourceLoaded(false);
        setloading(true);

        const request: SearchModel = {
            ...search,
            no: search.no ? search.no.toString() : ''
        }
        const response = await post<any>('api/portal/query-cancelled-av-info', request);
        if (response) {
            const cancelAvQueryResults = response.cancelAvQueryResults;
            setGrid(prev => ({
                ...prev,
                totalCount: cancelAvQueryResults ? cancelAvQueryResults.length : 0,
                items: cancelAvQueryResults ? cancelAvQueryResults : []
            }));
            setDataSourceLoaded(true);

            if (!cancelAvQueryResults || cancelAvQueryResults.length === 0) {
                setMessage(prev => ({
                    ...prev,
                    status: 'error',
                    text: response.description
                }));
            }
        } else {
            setGrid(initialGrid);
        }
        setloading(false);
    };

    const cancelPurchCase = async (avNo: any) => {
        const response = await post<any>('api/dealer/cancel-purchase', { AvNo: avNo });
        if (response) {
            setGridRequest(initialState);
            setSelectedTradeNo('');
            setShowCancelDialog(false);
            setDataSourceLoaded(false);
            setGridRequest(initialState);
            setMessage({
                status: 'success',
                text: `${avNo} alışveriş numarasına ait işlem başarılı bir şekilde iptal edilmiştir.`
            });
        }
    };

    const handleChange = (e: any) => {
        setGridRequest(prevState => ({
            ...prevState,
            [e.target.name]: e.target.value,
        }));
    };

    const handleNumericChange = (e: any, limit: number) => {

        let result = String(e.target.value).replace(/\D/g, '');
        if (result.length > limit) {
            result = result.slice(0, limit);
        }

        setGridRequest(prevState => ({
            ...prevState,
            [e.target.name]: result,
        }));
    };

    const handleResetForm = () => {
        setGridRequest(initialState);
        setGrid(initialGrid);
        setDataSourceLoaded(false);
    };

    const validate = (model: SearchModel): boolean => {
        let result = true;
        const no = model.no.toString();

        if (no === '') {
            result = false;
            setErrorText('Boş bırakılamaz');
        }
        else if ((model.criteria === 1 || model.criteria === 3) && no.length !== 11) {
            result = false;
            setErrorText('11 karakter uzunluğunda olmalıdır');
        }
        else if ((model.criteria === 4) && no.length !== 20) {
            result = false;
            setErrorText('20 karakter uzunluğunda olmalıdır');
        }
        return result;
    };

    const CancelDialog = (props: PropsWithChildren<any>) => {
        const [loadingAction, setLoadingAction] = useState(false);

        const handleApproveAction = () => {
            setLoadingAction(true);
            props.onApproveClick();
        };

        const handleClose = () => {
            setShowCancelDialog(false);
            setSelectedTradeNo('');
        };

        return (
            <Dialog
                open={props.show}
                onClose={(event: any, reason: string) => {
                    if (reason === 'backdropClick' && !loadingAction) {
                        handleClose();
                    }
                }}>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {props.tradeNo} nolu Alışverişi İptal etmek istediğinize emin misiniz?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <CustomButton
                        text="ALIŞVERİŞİ İPTAL ET"
                        className='btn-red'
                        loading={loadingAction}
                        disabled={loadingAction}
                        onClick={handleApproveAction}
                    />
                    <CustomButton
                        text="Vazgeç"
                        className='btn-secondary'
                        disabled={loadingAction}
                        onClick={handleClose}
                    />
                </DialogActions>
            </Dialog>
        );
    };

    const ActionButtons = (row: CancelledAvQueryResult) => {
        return (
            <>
                <CustomIconButton
                    className='mr-2'
                    disabled={IsNullOrEmpty(row.tradeNo)}
                    icon={<CancelSharp color='error' />}
                    onClick={() => {
                        if (!IsNullOrEmpty(row.tradeNo)) {
                            setSelectedTradeNo(row.tradeNo);
                            setShowCancelDialog(true);
                        }
                    }} />
            </>
        )
    }

    const headCells: DataTableCol<CancelledAvQueryResult>[] = [
        { id: 'tradeNo', label: 'Alışveriş No' },
        { id: 'memberName', label: 'Üye Adı SoyAdı' },
        { id: 'tradeDate', label: 'Alışveriş Tarihi' },
        { id: 'tradeTotal', label: 'Net Ödenen' },
        { id: 'tradeDiscountTotal', label: 'İndirim Tutarı' },
        { id: 'productName', label: 'Ürün/Hizmet' },
        {
            id: 'actions',
            label: 'İşlemler',
            align: 'right',
            width: 100,
            renderCell: (rowData, fieldName, value) => ActionButtons(rowData)
        }
    ];

    return (
        <>
            <Grid container direction="column" alignItems="center">
                <Box className="member-search" p={2}>
                    <Box className="form-field" mb={2}>
                        <Select
                            id="criteria"
                            name="criteria"
                            onChange={handleChange}
                            caption="Arama Kriteri"
                            className="densed-select"
                            options={{
                                data: queryTypesData,
                                displayField: 'Name',
                                displayValue: 'Id',
                                selected: gridRequest.criteria,
                            }}
                        />
                    </Box>
                    <Box className="form-field" mb={2}>
                        <Input
                            id="no"
                            name="no"
                            caption="No giriniz"
                            onChange={(e) => {
                                const limit =
                                    gridRequest.criteria === 1 || gridRequest.criteria === 3 ? 11
                                        : gridRequest.criteria === 4 ? 20 : 9999;
                                handleNumericChange(e, limit);
                            }}
                            value={gridRequest.no}
                            errorText={errorText}
                        />
                    </Box>
                    <Grid container spacing={1} justifyContent="center">
                        <Grid item>
                            <SearchButton
                                disabled={loading}
                                loading={loading}
                                onClick={() => loadGridAvInfo(gridRequest)}
                            />
                        </Grid>
                        <Grid item>
                            <ClearButton
                                disabled={loading || gridRequest.no === ''}
                                onClick={handleResetForm}
                            />
                        </Grid>
                    </Grid>
                </Box>
                {loading ? (
                    <Box pt={1} width="90%">
                        <Skeleton animation="wave" height={60} />
                        <Skeleton animation="wave" height={60} />
                        <Skeleton animation="wave" height={60} />
                        <Skeleton animation="wave" height={60} />
                    </Box>
                ) :
                    message ? (
                        <Alert severity={message.status}>{message.text}</Alert>
                    ) : dataSourceLoaded && grid.items.length > 0 ? (
                        <Box px={2} py={2} width="100%">
                            <DataTable
                                data={grid}
                                cells={headCells}
                                disableSort
                                disablePagination
                            />
                        </Box>
                    ) : null
                }
            </Grid>
            <CancelDialog show={showCancelDialog} tradeNo={selectedTradeNo} onApproveClick={() => cancelPurchCase(selectedTradeNo)} />
        </>
    );
};

Search.propTypes = {
    change: PropTypes.bool,
};

export default Search;
